import { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { deleteClassroom } from "@packages/api/madlenAPI";
import { useAppDispatch } from "@packages/store/hooks";
import { getCurrentTeacher } from "@packages/store/userSlice";
import { useToast } from "@packages/contexts/ToastContext";
import Modal from "@core/modal";
import { AlertTriangle } from "lucide-react";

interface DeleteClassroomModalProps {
  show: boolean;
  onClose: () => void;
  classroomId: number | null;
  classroomName: string;
  onDeleted: () => void;
}

const DeleteClassroomModal = ({
  show,
  onClose,
  classroomId,
  classroomName,
  onDeleted,
}: DeleteClassroomModalProps) => {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const [loading, setLoading] = useState(false);
  const [countdown, setCountdown] = useState(3);
  const [isTimerActive, setIsTimerActive] = useState(false);
  const { addToast } = useToast();

  // Start countdown when modal is shown
  useEffect(() => {
    if (show && !isTimerActive) {
      setCountdown(3);
      setIsTimerActive(true);
    }

    if (!show) {
      setIsTimerActive(false);
      setCountdown(3);
    }
  }, [show, isTimerActive]);

  // Handle countdown timer
  useEffect(() => {
    let timer: ReturnType<typeof setTimeout>;

    if (isTimerActive && countdown > 0) {
      timer = setTimeout(() => {
        setCountdown(countdown - 1);
      }, 1000);
    }

    return () => {
      if (timer) clearTimeout(timer);
    };
  }, [countdown, isTimerActive]);

  const handleDelete = async () => {
    if (!classroomId) return;

    try {
      setLoading(true);
      await deleteClassroom(classroomId);

      // Refresh teacher data to update classroom list
      dispatch(getCurrentTeacher());

      addToast({
        variant: "success",
        title: t("students.actions.deleteSuccess"),
        description: t("students.actions.deleteSuccessDesc", {
          name: classroomName,
        }),
        duration: 3000,
      });

      onDeleted();
      onClose();
    } catch (error) {
      console.error("Error deleting classroom:", error);
      addToast({
        variant: "error",
        title: t("common.error"),
        description: t("students.actions.deleteError"),
        duration: 3000,
      });
    } finally {
      setLoading(false);
    }
  };

  const actions = [
    {
      label: t("common.cancel"),
      onClick: onClose,
      variant: "secondary",
    },
    {
      label:
        countdown > 0
          ? `${t("common.delete")} (${countdown}${t("common.sec") || "s"})`
          : t("common.delete"),
      onClick: handleDelete,
      variant: "danger",
      loading: loading,
      disabled: countdown > 0,
    },
  ];

  return (
    <Modal
      id="delete-classroom-modal"
      show={show}
      onClose={onClose}
      title={t("students.actions.deleteClassroom")}
      actions={actions as any}
    >
      <div className="flex flex-col items-center text-center p-4 space-y-4">
        <div className="rounded-full bg-amber-100 p-3 dark:bg-amber-900">
          <AlertTriangle className="w-6 h-6 text-amber-600 dark:text-amber-400" />
        </div>
        <p className="text-gray-700 dark:text-gray-300">
          {t("students.actions.deleteConfirmation", { name: classroomName })}
        </p>
        <p className="text-sm text-red-500 dark:text-red-400 font-medium">
          {t("students.actions.deleteWarning")}
        </p>
      </div>
    </Modal>
  );
};

export default DeleteClassroomModal;
